'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { useScrollLock } from '../hooks/useScrollLock';

/**
 * Context to override the portal target for BottomSheet.
 * When set, the sheet portals into this container (e.g. a shadow DOM node)
 * instead of document.body. The page behind is still scroll-locked.
 */
/** Panel slide-down is 300ms; hold painting slightly past it so the close
 *  animation finishes before the sheet stops rendering. */
const CLOSE_PAINT_MS = 320;

const PortalContainerContext = createContext<HTMLElement | null>(null);

/** Hook to read the portal container from context (null if not inside a provider). */
export function usePortalContainer() {
  return useContext(PortalContainerContext);
}

export function PortalContainerProvider({
  container,
  children,
}: {
  container: HTMLElement;
  children: ReactNode;
}) {
  return (
    <PortalContainerContext.Provider value={container}>{children}</PortalContainerContext.Provider>
  );
}

/**
 * Z-index scale for UI overlays:
 * - z-10: Footer (static, relative positioned)
 * - z-40-45: SimulationBanner, alerts
 * - z-50: Header (fixed), modals, sidebars
 * - z-60: BottomSheet (above header for proper overlay)
 * - z-70+: Tooltips, urgent overlays (reserved)
 */

interface BottomSheetProps {
  /** Whether the bottom sheet is open */
  open: boolean;
  /** Callback when the sheet should close */
  onClose: () => void;
  /** Content to render inside the sheet (scrollable) */
  children: ReactNode;
  /** Optional header content rendered in the sticky drag area (non-scrollable) */
  header?: ReactNode;
  /**
   * Pinned below the scroll area, always visible. For the commit control of a
   * sheet that decides something ("Save"), which must never be something the
   * user has to find by scrolling.
   *
   * Has to live here rather than as a `sticky` element inside `children`:
   * SheetNavigator wraps page content in `overflow-hidden` for its page
   * transitions, and an `overflow` ancestor becomes the scrollport a sticky
   * child sticks within, so it scrolls away with the content.
   */
  footer?: ReactNode;
  /** Additional class names for the sheet panel */
  className?: string;
  /** Distance threshold in pixels to close when released (default: 100) */
  closeThreshold?: number;
  /** Velocity threshold (px/ms) to close regardless of distance (default: 0.5) */
  velocityThreshold?: number;
  /** Hide the default swipe handle (useful when rendering custom handle in content) */
  hideHandle?: boolean;
  /** Maximum height of the sheet (default: '90dvh') */
  maxHeight?: string;
  /** Z-index class override (default: 'z-[60]') */
  zIndex?: string;
  /** Show scroll indicator when content is scrollable (default: true) */
  showScrollIndicator?: boolean;
  /** Use light theme for scroll indicator (for light content backgrounds) */
  lightScrollIndicator?: boolean;
  /** Inline styles for the sheet panel (merged with internal styles) */
  panelStyle?: React.CSSProperties;
  /** Callback fired with scrollTop when content area scrolls */
  onContentScroll?: (scrollTop: number) => void;
  /** When this key changes, scroll is reset to top (e.g. for page navigation) */
  resetScrollKey?: string;
}

/**
 * A mobile-friendly bottom sheet component with drag-to-close functionality.
 *
 * Features:
 * - Slides up from bottom
 * - Drag down on handle/header area to close (follows finger, Instagram-style)
 * - Velocity-based close detection (flick to dismiss)
 * - Snaps back if not dragged far enough
 * - Tap backdrop to close
 * - Escape key to close
 * - Prevents body scroll when open (content inside can still scroll)
 * - Rounded top corners with swipe handle indicator
 * - Optional sticky header in drag area
 */
export function BottomSheet({
  open,
  onClose,
  children,
  header,
  footer,
  className = '',
  closeThreshold = 100,
  velocityThreshold = 0.5,
  hideHandle = false,
  maxHeight = '90dvh',
  zIndex = 'z-[60]',
  showScrollIndicator = true,
  lightScrollIndicator = false,
  panelStyle,
  onContentScroll,
  resetScrollKey,
}: BottomSheetProps) {
  const dragAreaRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const keyboard = useKeyboardInset(open, scrollContainerRef);
  const touchStartY = useRef<number | null>(null);
  const touchStartTime = useRef<number | null>(null);
  // True while a pull-down on the CONTENT (not the drag area) has taken over
  // as a sheet drag — see the body drag-to-close listeners below.
  const bodyDragEngaged = useRef(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showIndicator, setShowIndicator] = useState(false);
  const [mounted, setMounted] = useState(false);
  const portalContainer = useContext(PortalContainerContext);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Check if content is scrollable and update indicator
  const checkScrollIndicator = useCallback(() => {
    if (!showScrollIndicator) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const hasMoreContent = container.scrollHeight > container.clientHeight + 5;
    const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 20;

    setShowIndicator(hasMoreContent && !isAtBottom);
  }, [showScrollIndicator]);

  // Set up scroll indicator tracking
  useEffect(() => {
    if (!showScrollIndicator || !open) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    // Check initially and after delays for content rendering
    checkScrollIndicator();
    const timeouts = [
      setTimeout(checkScrollIndicator, 50),
      setTimeout(checkScrollIndicator, 150),
      setTimeout(checkScrollIndicator, 300),
    ];

    const handleScroll = () => {
      checkScrollIndicator();
      onContentScroll?.(container.scrollTop);
    };

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkScrollIndicator);

    const resizeObserver = new ResizeObserver(checkScrollIndicator);
    resizeObserver.observe(container);
    if (container.firstElementChild) {
      resizeObserver.observe(container.firstElementChild);
    }

    return () => {
      timeouts.forEach(clearTimeout);
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkScrollIndicator);
      resizeObserver.disconnect();
    };
    // `mounted`: see the touch effects below; the container does not exist on
    // the first render, so a sheet that mounts already open needs a second run.
  }, [open, showScrollIndicator, checkScrollIndicator, onContentScroll, mounted]);

  // Reset drag offset when sheet closes
  useEffect(() => {
    if (!open) {
      setDragOffset(0);
      setIsDragging(false);
    }
  }, [open]);

  // Reset scroll position when resetScrollKey changes (e.g. page navigation)
  useEffect(() => {
    if (resetScrollKey == null) return;
    const container = scrollContainerRef.current;
    if (container) container.scrollTop = 0;
  }, [resetScrollKey]);

  // The page behind holds still while open, in a widget's shadow DOM too:
  // the sheet covers the page, so it owns the page's scroll until it closes.
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  // Prevent scroll propagation at content boundaries (iOS Safari fix).
  // overscroll-contain alone doesn't reliably prevent scroll-through on iOS,
  // so we manually block touchmove when at the top/bottom of the scroll area.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !open) return;

    let startY = 0;

    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      startY = touch.clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      const deltaY = touch.clientY - startY;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtTop = scrollTop <= 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

      // Block scroll-through when at boundaries or content doesn't overflow
      if ((isAtTop && deltaY > 0) || (isAtBottom && deltaY < 0)) {
        e.preventDefault();
      }
    };

    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
    };
    // `mounted`: the first render returns null (see below), so on a sheet that
    // mounts already open this effect runs before the container exists. It has
    // to run again once it does, or the listeners never attach.
  }, [open, mounted]);

  // Native touch event listeners for drag-to-close (on drag area: handle + header)
  useEffect(() => {
    const dragArea = dragAreaRef.current;
    const container = scrollContainerRef.current;
    if (!dragArea || !open) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      touchStartY.current = touch.clientY;
      touchStartTime.current = Date.now();
      setIsDragging(true);
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Prevent default only on drag area to allow content scrolling
      e.preventDefault();
      if (touchStartY.current === null) return;

      const touch = e.touches[0];
      if (!touch) return;
      const deltaY = touch.clientY - touchStartY.current;
      // Only allow dragging down (positive deltaY), clamp at 0
      setDragOffset(Math.max(0, deltaY));
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartY.current === null || touchStartTime.current === null) {
        setIsDragging(false);
        return;
      }

      const ended = e.changedTouches[0];
      if (!ended) return;
      const deltaY = ended.clientY - touchStartY.current;
      const deltaTime = Date.now() - touchStartTime.current;
      const velocity = deltaTime > 0 ? deltaY / deltaTime : 0; // px per ms

      // Close if dragged past threshold OR flicked with enough velocity
      if (deltaY > closeThreshold || velocity > velocityThreshold) {
        onClose();
      }

      // Always snap back, including when we just asked to close: `onClose` is
      // allowed to decline (a sheet with unsaved changes keeps itself open to
      // warn), and a declined drag that kept its offset left the panel parked
      // half off-screen. When the close IS honored, `open` flips false and the
      // panel translates fully out, so releasing the offset costs nothing.
      setDragOffset(0);

      touchStartY.current = null;
      touchStartTime.current = null;
      setIsDragging(false);
    };

    dragArea.addEventListener('touchstart', handleTouchStart, { passive: true });
    dragArea.addEventListener('touchmove', handleTouchMove, { passive: false });
    dragArea.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Body drag-to-close: when the content is scrolled to the very top,
    // pulling DOWN anywhere on it drags the sheet closed too — you don't
    // have to reach for the handle. While the content is scrolled, every
    // move re-anchors the start point instead, so in-sheet scrolling keeps
    // its normal gesture; only a fresh pull from the top engages the drag.
    const bodyTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      bodyDragEngaged.current = false;
      touchStartY.current = touch.clientY;
      touchStartTime.current = Date.now();
    };

    const bodyTouchMove = (e: TouchEvent) => {
      if (touchStartY.current === null || !container) return;
      const moved = e.touches[0];
      if (!moved) return;
      if (container.scrollTop > 0) {
        bodyDragEngaged.current = false;
        touchStartY.current = moved.clientY;
        touchStartTime.current = Date.now();
        return;
      }
      const deltaY = moved.clientY - touchStartY.current;
      if (deltaY > 0) {
        e.preventDefault();
        bodyDragEngaged.current = true;
        setIsDragging(true);
        setDragOffset(deltaY);
      }
    };

    const bodyTouchEnd = (e: TouchEvent) => {
      if (!bodyDragEngaged.current) {
        touchStartY.current = null;
        touchStartTime.current = null;
        return;
      }
      bodyDragEngaged.current = false;
      handleTouchEnd(e);
    };

    container?.addEventListener('touchstart', bodyTouchStart, { passive: true });
    container?.addEventListener('touchmove', bodyTouchMove, { passive: false });
    container?.addEventListener('touchend', bodyTouchEnd, { passive: true });

    return () => {
      dragArea.removeEventListener('touchstart', handleTouchStart);
      dragArea.removeEventListener('touchmove', handleTouchMove);
      dragArea.removeEventListener('touchend', handleTouchEnd);
      container?.removeEventListener('touchstart', bodyTouchStart);
      container?.removeEventListener('touchmove', bodyTouchMove);
      container?.removeEventListener('touchend', bodyTouchEnd);
    };
    // `mounted`: same reason as the scroll-through effect above. Without it a
    // sheet rendered with `open` already true (mounted on demand rather than
    // toggled) had no drag-to-close until some other dependency happened to
    // change, which on the locations widget was the next live-status poll.
  }, [open, onClose, closeThreshold, velocityThreshold, mounted]);

  // Calculate transform based on open state and drag
  const panelTransform = open ? `translateY(${dragOffset}px)` : 'translateY(100%)';

  /**
   * Whether the sheet should paint at all. Tracks `open`, but lags it on the way
   * out so the panel's 300ms slide-down still plays — flipping `invisible` the
   * instant `open` goes false would make the sheet disappear rather than close.
   *
   * Starts false so a never-opened sheet never paints: that's the whole point
   * (see the container's className comment).
   */
  const [painted, setPainted] = useState(open);
  useEffect(() => {
    if (open) {
      setPainted(true);
      return;
    }
    const t = setTimeout(() => setPainted(false), CLOSE_PAINT_MS);
    return () => clearTimeout(t);
  }, [open]);

  if (!mounted) return null;

  // Portal to document.body so the sheet escapes any parent stacking context
  // (e.g. the header wrapper's z-50) and z-[60] works against the root.
  return createPortal(
    <div
      // No opacity fade on this container: an ancestor with opacity < 1
      // isolates the backdrop into an offscreen group, so its backdrop-filter
      // would blur nothing until the fade finished (i.e. only once the sheet
      // was all the way up). Visibility is carried entirely by the children —
      // the backdrop's own color/blur and the panel's transform.
      //
      // `invisible` while closed, though, because a closed panel is only
      // translateY(100%) away — it still PAINTS, and `shadow-2xl` on its
      // `rounded-t-[2rem]` top edge bleeds back up into the bottom of the
      // screen. One sheet is nearly invisible; a page with nine mounted sheets
      // stacks nine shadows into two distinct curved bands pinned above the
      // browser chrome, with no obvious cause. visibility (not opacity) is the
      // right tool twice over: it suppresses painting including shadows, it
      // doesn't create an offscreen group like opacity would, and it keeps the
      // panel measurable for anything that sizes it while closed.
      className={`fixed inset-0 ${zIndex} ${painted ? 'visible' : 'invisible'} ${
        open ? 'pointer-events-auto' : 'pointer-events-none'
      }`}
    >
      {/* Backdrop — dim + blur the page behind so the sheet reads as the only
          focused surface (blur is base behavior for every sheet).

          The blur ramps in via backdrop-filter + background-color while the
          element stays at opacity 1. Fading opacity here instead would
          suppress the blur until the transition ended (offscreen-group
          isolation), which read as the blur "popping in" only after the sheet
          finished rising. -webkit- prefix + its own transition entry keep
          this working (and animating) on iOS Safari. */}
      <div
        className="absolute inset-0"
        style={{
          touchAction: 'none',
          backgroundColor: open ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0)',
          backdropFilter: open ? 'blur(4px)' : 'blur(0px)',
          WebkitBackdropFilter: open ? 'blur(4px)' : 'blur(0px)',
          transition:
            'background-color 300ms ease, backdrop-filter 300ms ease, -webkit-backdrop-filter 300ms ease',
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={`absolute inset-x-0 bottom-0 flex flex-col rounded-t-[2rem] shadow-2xl ${
          isDragging ? '' : 'transition-transform duration-300 ease-out'
        } ${className}`}
        style={{
          ...panelStyle,
          transform: panelTransform,
          // With the on-screen keyboard up, sit on top of it and fit in what
          // is left of the screen — otherwise the keyboard covers the fields.
          bottom: keyboard.inset || undefined,
          // While typing, take the WHOLE space above the keyboard, top to
          // bottom: a short sheet pinned to the keyboard leaves the focused
          // field at its bottom edge, where a dropdown or suggestion list
          // under it has nowhere to open. (iOS: the visible height above the
          // keyboard. Android shrinks the page for the keyboard instead, so
          // the container's own height already is that space.)
          ...(keyboard.typing
            ? {
                height: keyboard.inset
                  ? `${Math.max(keyboard.visibleHeight - 16, 160)}px`
                  : 'calc(100% - 16px)',
                maxHeight: 'none',
              }
            : {
                maxHeight: keyboard.inset
                  ? `min(${maxHeight}, ${Math.max(keyboard.visibleHeight - 16, 160)}px)`
                  : maxHeight,
              }),
          overscrollBehavior: 'contain',
        }}
      >
        {/* Drag area: handle + optional header - pulling here closes the sheet */}
        <div
          ref={dragAreaRef}
          className="relative shrink-0 cursor-grab overflow-hidden rounded-t-[2rem]"
          style={{ touchAction: 'none' }}
        >
          {/* Optional sticky header */}
          {header}

          {/* Swipe handle indicator — overlaid on top of header for edge-to-edge content */}
          {!hideHandle && (
            <div className="absolute inset-x-0 top-0 z-10 flex justify-center pt-3 pb-2">
              <div className="h-1.5 w-14 rounded-full bg-white/50 shadow-sm" />
            </div>
          )}
        </div>

        {/* Scrollable content area */}
        <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {children}
        </div>

        {/* Pinned footer — outside the scroll area, so it stays put. Above the
            scroll indicator in the stack: the indicator is absolutely
            positioned to the panel's bottom edge, which is now the footer. */}
        {footer && <div className="relative z-20 shrink-0">{footer}</div>}

        {/* Scroll indicator */}
        <AnimatePresence>
          {/* A footer occupies the panel's bottom edge, where this gradient
              anchors, so it would render entirely behind it. The footer is its
              own bottom boundary. */}
          {showIndicator && !footer && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={`pointer-events-none absolute right-0 bottom-0 left-0 flex h-20 items-end justify-center ${
                lightScrollIndicator
                  ? 'bg-gradient-to-t from-[var(--card)] to-transparent'
                  : 'bg-gradient-to-t from-[#1f2220] to-transparent'
              }`}
            >
              <div
                className={`mb-3 flex flex-col items-center gap-1 ${
                  lightScrollIndicator ? 'text-muted-foreground' : 'text-white/50'
                }`}
              >
                <span className="text-xs font-medium tracking-wide uppercase">Scroll for more</span>
                <motion.div
                  animate={{ y: [0, 4, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <ChevronLeft className="h-4 w-4 -rotate-90" />
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>,
    portalContainer ?? document.body
  );
}

/**
 * How much of the bottom of the screen the on-screen keyboard covers, while the
 * sheet is open. iOS Safari doesn't shrink the layout viewport for the
 * keyboard, so a `fixed bottom-0` sheet stays underneath it; the visual
 * viewport is the only thing that knows. (Android Chrome resizes the layout
 * viewport, so the inset reads 0 there and nothing changes.)
 *
 * `typing` is whether a text field inside the sheet has focus — the cue to
 * stretch the sheet over everything above the keyboard. It's read from focus,
 * not from the inset, so it works on Android too, where the inset is 0.
 *
 * Also brings the focused field into view inside the sheet's own scroller once
 * the keyboard has finished opening — the body is scroll-locked, so the
 * browser can't do it by scrolling the page. It goes to the TOP of the visible
 * area, not just into view: a field scrolled only to the bottom edge sits on
 * the keyboard with no room for the list it opens.
 */
function useKeyboardInset(open: boolean, scrollRef: RefObject<HTMLDivElement | null>) {
  const [state, setState] = useState({ inset: 0, visibleHeight: 0 });
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!open || !vv) {
      setState({ inset: 0, visibleHeight: 0 });
      setTyping(false);
      return;
    }
    const update = () => {
      const inset = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      // Under ~80px is browser chrome moving, not a keyboard.
      setState(inset > 80 ? { inset, visibleHeight: vv.height } : { inset: 0, visibleHeight: 0 });
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);

    // Text-entry fields only: a checkbox or a select doesn't raise a keyboard.
    const isTextField = (el: Element | null): el is HTMLElement =>
      !!el &&
      scrollRef.current?.contains(el) === true &&
      ((el instanceof HTMLElement && el.isContentEditable) ||
        el.matches(
          'textarea, input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]):not([type=range]):not([type=file]):not([type=color])'
        ));

    let timer: ReturnType<typeof setTimeout> | undefined;
    let blurTimer: ReturnType<typeof setTimeout> | undefined;
    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null;
      if (!isTextField(el)) return;
      clearTimeout(blurTimer);
      setTyping(true);
      clearTimeout(timer);
      // After the keyboard and the stretched sheet have settled.
      timer = setTimeout(() => el.scrollIntoView({ block: 'start', behavior: 'smooth' }), 320);
    };
    const onFocusOut = () => {
      // Moving between two fields fires out-then-in; only drop back once focus
      // has really left the sheet's text fields.
      clearTimeout(blurTimer);
      blurTimer = setTimeout(() => setTyping(isTextField(document.activeElement)), 120);
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);

    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      clearTimeout(timer);
      clearTimeout(blurTimer);
    };
  }, [open, scrollRef]);

  return { ...state, typing };
}
